import { lazy, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageCircle, X } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { useChatBot } from '@/contexts/ChatBotContext'
import { useCookieConsent } from '@/contexts/CookieConsentContext'
import { useOverlay } from './OverlayProvider'
import { ShellDialog } from './ShellDialog'

const DeferredChatBot = lazy(async () => ({
  default: (await import('@/components/ChatBot/DeferredChatBot')).DeferredChatBot,
}))

/** Adapt the existing chat and cookie contexts to the shared modal layer. */
export function SupportWidgets() {
  const { t } = useTranslation('common')
  const { active, open, close } = useOverlay()
  const { setIsOpen } = useChatBot()
  const { showBanner, preferences, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [draft, setDraft] = useState(preferences)
  const [saved, setSaved] = useState(false)
  useEffect(() => { setIsOpen(active === 'chat') }, [active, setIsOpen])
  useEffect(() => { if (active === 'cookies') { setDraft(preferences); setSaved(false) } }, [active, preferences])
  const save = () => { savePreferences(draft); setSaved(true); close() }
  return <>
    {showBanner && <aside className="cookie-notice" role="dialog" aria-modal="false" aria-labelledby="cookie-notice-title" aria-describedby="cookie-notice-description">
      <div className="cookie-notice-copy">
        <h2 id="cookie-notice-title">{t('cookie.title')}</h2>
        <p id="cookie-notice-description">{t('cookie.message')} <LocalizedLink to="privacy">{t('cookie.learnMore')}</LocalizedLink></p>
      </div>
      <div className="cookie-notice-actions">
        <button className="button" onClick={acceptAll}>{t('cookie.acceptAll')}</button>
        <button className="button button-secondary" onClick={rejectAll}>{t('cookie.rejectAll')}</button>
        <button className="text-button" onClick={() => open('cookies')}>{t('cookie.customize')}</button>
      </div>
    </aside>}
    <p className="sr-only" role="status">{saved ? t('cookie.saved') : ''}</p>
    <button className={`chat-launcher${active === 'chat' ? ' chat-launcher--open' : ''}`} onClick={() => active === 'chat' ? close() : open('chat')} aria-label={active === 'chat' ? t('aria.close') : t('chatbot.open')} aria-controls="support-chat" aria-expanded={active === 'chat'}>
      <span className="chat-launcher-icon" aria-hidden="true">
        <MessageCircle className="chat-launcher-open-icon" />
        <X className="chat-launcher-close-icon" />
      </span>
      <span className="chat-launcher-label">{t('chatbot.launcherLabel')}</span>
    </button>
    <ShellDialog id="support-chat" open={active === 'chat'} onClose={close} title={t('chatbot.title')} className="chat-dialog" modal={false}>
      {active === 'chat' && <Suspense fallback={null}><DeferredChatBot /></Suspense>}
    </ShellDialog>
    <ShellDialog id="cookie-preferences" open={active === 'cookies'} onClose={close} title={t('cookie.preferencesTitle')}>
      <p>{t('cookie.preferencesDescription')}</p>
      <div className="cookie-option"><div><strong>{t('cookie.necessary')}</strong><p>{t('cookie.necessaryDesc')}</p></div><span>{t('cookie.alwaysOn')}</span></div>
      {(['analytics', 'marketing'] as const).map((key) => <label className="cookie-option" key={key}><div><strong>{t(`cookie.${key}`)}</strong><p>{t(`cookie.${key}Desc`)}</p></div><input type="checkbox" checked={draft[key]} onChange={(event) => setDraft({ ...draft, [key]: event.target.checked })} /></label>)}
      <div className="button-row"><button className="button button-secondary" onClick={close}>{t('cookie.cancel')}</button><button className="button" onClick={save}>{t('cookie.savePreferences')}</button></div>
    </ShellDialog>
  </>
}
